import * as angular from 'angular';
import * as template from './template.html';
import './styling.scss';

import { HippoApiService } from 'services/hippoApi';
import { IRootScopeService, ITimeoutService } from 'angular';
import { formatAsDollars } from 'common/source/utilities/strings';
import { CurrentUserService } from 'services/currentUser';

import * as _ from 'lodash';
import { getLongNumericAmericanDateString, getShortNumericAmericanDateString } from 'common/source/utilities/dates';
import { PermissionService } from 'services/permission';
import { ModalService } from 'common/source/services/modal';
import { paymentOptions } from 'utilities/paymentsHelper';

const module = angular.module('ach-pay-now', []);

interface AchPayNow extends angular.IScope {
    achForm: any;
}

interface AchPaymentMethodScope extends angular.IScope {
    achForm: angular.IFormController;
}

const PAYMENT_STATUSES_TO_RECHARGE_UPON_ACH_UPDATE: Array<PaymentStatus> = [
    'due',
    'failed',
    'not_received',
    'outstanding'
];

const STRIPE_CARD_ERRORS_ELEMENT_SELECTOR = '#card-error';

const paymentFrequencyToDisplayMap = {
    annually: 'Annual',
    quarterly: 'Quarterly',
    monthly: 'Monthly',
    semi_annually: 'Semi Annual'
};

interface AchPayNowController extends angular.IStrictController {
    isLastFailedPaymentSelected: () => boolean;
    canOnlyPayLastFailedPaymentOption: boolean;
    policyInfo: CustomerPolicyInfo;
    edit: () => void;
    isEditMode: boolean;
    cancel: () => void;
    payNow: () => void;
    isUpcomingSelected: () => void;
    isRemainingSelected: () => void;
    selectedPaymentAmount: string;
    canCancelEditMode: boolean;
    showSpinner: boolean;
    showEditPaymentButton: boolean;
    hasFailedPay: boolean;
    frequency: string;
    paymentDueDate?: string;
    newPaymentDueDate?: string;
    nextUnpaidAmount: string;
    remainingAmount: string;
    shouldShowFailureMessage: boolean;
    successfulPaymentFlag: boolean;
    saveNewAchAndPayNow: (result: stripe.TokenResponse | undefined) => void;
    updateHasChangedAchInfo: () => boolean;
    lastFourDigits: string;
    routingNumber: string;
    setSuccessMessage: (successMessage: string, showSaveAsDefaultPaymentMethodQuestion: boolean) => void;
    closePayNow: () => void;
    achDetails: AchDetails;
}

module.component('achPayNow', {
    bindings: {
        policyInfo: '<',
        setSuccessMessage: '<',
        closePayNow: '<',
        canOnlyPayLastFailedPaymentOption: '<'
    },
    template: template,
    // tslint:disable-next-line:no-big-function
    controller: function(
        this: AchPayNowController,
        $scope: AchPayNow,
        $stateParams: PolicyStateParams,
        $rootScope: IRootScopeService,
        hippoApiService: HippoApiService,
        currentUserService: CurrentUserService,
        modalService: ModalService,
        $timeout: ITimeoutService,
        $element: angular.IAugmentedJQuery,
        permissionService: PermissionService
    ) {
        this.$onInit = () => {
            this.successfulPaymentFlag = false;
            this.canCancelEditMode = true;
            this.selectedPaymentAmount = this.policyInfo.next_unpaid_payment_amount ? 'next_unpaid' : 'remaining';
            this.hasFailedPay = hasPastFailedPayment();
            updateFrequencyInfo();
        };

        $rootScope.$on('updatedCustomerPolicyInfos', () => {
            updateFrequencyInfo();
        });

        const updateFrequencyInfo = () => {
            const policyIndex = $stateParams.policyIndex;
            currentUserService
                .getCustomerPolicyInfoByIndex(policyIndex)
                .then((policyInfo) => {
                    this.nextUnpaidAmount = formatAsDollars(policyInfo.next_unpaid_payment_amount, 2);
                    this.paymentDueDate = policyInfo.next_unpaid_payment_due_date
                        ? getShortNumericAmericanDateString(policyInfo.next_unpaid_payment_due_date)
                        : '';

                    this.remainingAmount = formatAsDollars(policyInfo.remaining_payment_amount, 2);
                    this.frequency = paymentFrequencyToDisplayMap[policyInfo.checkout_data.payment_frequency];
                    this.hasFailedPay = hasPastFailedPayment(policyInfo);
                    this.showSpinner = false;
                })
                .then((policyInfo) => {
                    if (this.successfulPaymentFlag) {
                        this.newPaymentDueDate = this.paymentDueDate;
                        showSuccessMessage();
                        modalService.closeAllDialogs();
                    }
                    // only update payment info at the end since its used to determine if ach info was updated
                    updatePaymentInformationDisplay();
                });
        };

        const hasPastFailedPayment = (policyInfo = this.policyInfo) =>
            _.includes(PAYMENT_STATUSES_TO_RECHARGE_UPON_ACH_UPDATE, policyInfo.payment_status);

        this.isUpcomingSelected = () => {
            return this.selectedPaymentAmount === paymentOptions.nextUnpaid;
        };

        this.isRemainingSelected = () => {
            return this.selectedPaymentAmount === paymentOptions.remaining;
        };

        this.isLastFailedPaymentSelected = () => {
            return this.selectedPaymentAmount === paymentOptions.lastFailedPayment;
        };

        const exitEditMode = () => {
            this.showSpinner = false;
            this.isEditMode = false;
            this.shouldShowFailureMessage = false;
            this.setSuccessMessage('', false);
            this.showEditPaymentButton = permissionService.canEditPaymentMethod(this.policyInfo) && !this.isEditMode;
        };

        this.edit = () => {
            this.isEditMode = true;
            this.setSuccessMessage('', false);
            this.shouldShowFailureMessage = false;
            this.showEditPaymentButton = false;
        };

        this.cancel = () => {
            exitEditMode();
            this.closePayNow();
        };

        const updateCustomerPolicyInfo = () => {
            currentUserService.getUpdatedCustomerPolicyInfos().then(() => {
                $rootScope.$emit('updatedCustomerPolicyInfos');
            });
        };

        const saveNewAchAndPayNow = () => {
            getBankAccountToken(this.achDetails)
                .then((result) => {
                    if (result && result.token) {
                        const tokenId = result.token.id;
                        const policyId = this.policyInfo.id;
                        const policyInfoUpdate: CustomerPolicyInfoUpdate = {
                            checkout_data: {
                                payment_method: 'ach',
                                payment_token: tokenId,
                                accepts_ach_terms: true
                            }
                        };
                        hippoApiService
                            .updateCustomerPolicy(policyId, policyInfoUpdate)
                            .then(() => {
                                payNowWithSavedAch();
                            })
                            .catch(() => {
                                showFailureMessage();
                            });
                    } else {
                        showFailureMessage();
                    }
                })
                .catch(() => {
                    showFailureMessage();
                });
        };

        this.payNow = () => {
            // hide failure messages when new pay now event is triggered
            this.shouldShowFailureMessage = false;
            this.showSpinner = true;

            if (this.isEditMode && this.updateHasChangedAchInfo()) {
                const form = $element.find('form').scope() as AchPaymentMethodScope;

                const achForm = form.achForm;
                achForm.$setSubmitted();
                $timeout(() => {
                    // If there is an error, focus on the first input with an error.
                    // Otherwise, exit the edit mode.
                    const inputWithError = document.querySelector('.ach-form .has-error input');

                    if (inputWithError) {
                        (inputWithError as HTMLElement).focus();
                        this.showSpinner = false;
                    } else {
                        saveNewAchAndPayNow();
                    }
                });
            } else {
                // if ach info is not changed, simply charge the selected amount to the saved checkout info
                payNowWithSavedAch();
            }
        };

        const showSuccessMessage = () => {
            let successMessage = 'Thank you for your payment!';
            if (this.updateHasChangedAchInfo()) {
                successMessage += `<br>Your default bank account information has been saved.`;
            }
            this.setSuccessMessage(successMessage, false);
        };

        const showFailureMessage = () => {
            this.showSpinner = false;
            this.shouldShowFailureMessage = true;
            // clear success message
            this.setSuccessMessage('', false);
        };

        const updatePaymentInformationDisplay = () => {
            const { last4 = '', account_holder_name = '', routing_number = '' }: AchInfoDetails = this.policyInfo
                .checkout_data.ach_info
                ? _.get(this, 'policyInfo.checkout_data.ach_info')
                : {};

            this.lastFourDigits = last4;
            this.routingNumber = routing_number;
            this.achDetails = {
                account_holder_name: account_holder_name,
                routing_number: routing_number,
                account_number: last4
            };
            this.showEditPaymentButton = permissionService.canEditPaymentMethod(this.policyInfo) && !this.isEditMode;
        };

        this.updateHasChangedAchInfo = () => {
            const achInfo = _.get(this, 'policyInfo.checkout_data.ach_info');
            if (!achInfo) {
                return false;
            }
            return (
                // Account number will almost always be different when new ach info is entered
                // since we only have the last 4 digits to compare with.
                this.achDetails.account_number !== achInfo.last4 ||
                this.achDetails.account_holder_name !== achInfo.account_holder_name ||
                this.achDetails.routing_number !== achInfo.routing_number
            );
        };

        const payNowWithSavedAch = () => {
            const policyNumber = this.policyInfo.id;
            const selected = {
                paymentOption: this.selectedPaymentAmount,
                paymentDueDate: getLongNumericAmericanDateString(this.paymentDueDate as AmericanDateString)
            };

            hippoApiService.updateFrequencyOption(policyNumber, selected).then(() => {
                // Final callback for all pay now paths
                // Sets successful payment flag to trigger show success message on update
                this.successfulPaymentFlag = true;
                updateCustomerPolicyInfo();
            }, showFailureMessage);
        };

        const getBankAccountToken = (accountData: AchDetails) => {
            const stripe = window.Stripe(this.policyInfo.checkout_data.stripe_key);
            const { account_holder_name, routing_number, account_number } = accountData;
            const bankAccountPayload: stripe.BankAccountTokenOptions = {
                country: 'US',
                currency: 'usd',
                account_holder_type: 'individual',
                account_holder_name,
                routing_number,
                account_number
            };
            return stripe.createToken('bank_account', bankAccountPayload);
        };
    }
});

export { module };
